import React from 'react';
import { Outlet, useLocation, Link as RouterLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined';
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import InsightsIcon from '@mui/icons-material/Insights';
import PsychologyAltIcon from '@mui/icons-material/PsychologyAlt';
import type { RootState } from '../store';
import { useAuth } from '../hooks/useAuth';

const drawerWidth = 240;

const navItems = [
  { label: 'Dashboard', to: '/', icon: <DashboardOutlinedIcon fontSize="small" /> },
  { label: 'Easyflex Sync', to: '/easyflex', icon: <SyncOutlinedIcon fontSize="small" /> },
  { label: 'DB Explorer', to: '/db', icon: <CodeOutlinedIcon fontSize="small" /> },
  { label: 'API Explorer', to: '/api', icon: <CodeOutlinedIcon fontSize="small" /> },
  { label: 'Approvals', to: '/approvals', icon: <DescriptionOutlinedIcon fontSize="small" /> },
  { label: 'Engagement', to: '/engagement', icon: <InsightsIcon fontSize="small" /> },
  { label: 'AI Observability', to: '/ai-observability', icon: <PsychologyAltIcon fontSize="small" /> },
];

export const AppShell: React.FC = () => {
  const { logout } = useAuth();
  const location = useLocation();
  const { user } = useSelector((state: RootState) => (state as any).auth);

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      <AppBar position="fixed" color="default" elevation={0} sx={{ borderBottom: '1px solid rgba(0,0,0,0.08)', zIndex: theme => theme.zIndex.drawer + 1 }}>
        <Toolbar>
          <Typography variant="h6" fontWeight={800} sx={{ flexGrow: 1 }}>
            Central DB Admin
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              {user?.username || user?.email || 'Signed in'}
            </Typography>
            <IconButton onClick={() => logout()} aria-label="Logout" size="small">
              <LogoutIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: drawerWidth,
            boxSizing: 'border-box',
            borderRight: '1px solid rgba(0,0,0,0.08)',
          },
        }}
      >
        <Toolbar />
        <Box sx={{ overflow: 'auto' }}>
          <List>
            {navItems.map(item => (
              <ListItemButton
                key={item.to}
                component={RouterLink}
                to={item.to}
                selected={location.pathname === item.to}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItemButton>
            ))}
          </List>
        </Box>
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Toolbar />
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};
